import { getRouteApi } from '@tanstack/react-router';
import { useTenantUsers } from '../../api/tenant-users';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { SearchIcon, Users2Icon } from 'lucide-react';
import { PersonRow } from './person-row';
import type { EntityAccessPerson } from '../types/types';

type PeoplePanelProps = {
  selectePerson: EntityAccessPerson | null;
  onSelectPerson: (person: EntityAccessPerson) => void;
};

export const PeoplePanel = ({ selectePerson, onSelectPerson }: PeoplePanelProps) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const route = getRouteApi('/_authenticated/_sidebar-layout/tenant/$tenantId/entity-access');
  const { tenantId } = route.useParams();
  const { data } = useTenantUsers({ id: tenantId });

  const people = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return data.employees.filter((employee) => {
      if (employee.role === 'admin') return false;
      if (!normalizedSearch) return true;

      const fullName = `${employee.user.firstName ?? ''} ${employee.user.lastName ?? ''}`;
      const email = employee.user.email ?? '';

      return `${fullName} ${email}`.toLowerCase().includes(normalizedSearch);
    });
  }, [data.employees, search]);

  return (
    <aside className="overflow-hidden rounded-md border border-background-darker bg-white text-primary-darker">
      <div className="flex items-center gap-3 border-b border-background-darker bg-background-lighter/50 px-4 py-3">
        <Users2Icon aria-hidden="true" className="size-4 shrink-0" />
        <h2 className="text-md font-semibold leading-7">{t('people')}</h2>
      </div>
      <div className="border-b border-background-darker p-4">
        <Input
          size="sm"
          placeholder={t('search')}
          className="h-9 rounded-sm border-background-darker text-md font-normal leading-6 placeholder:text-text-secondary focus-visible:border-2 focus-visible:border-primary-light focus-visible:ring-primary-light/20"
          startIcon={<SearchIcon aria-hidden="true" className="size-4 text-text-secondary" />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="space-y-1 p-4">
        {people?.map((person) => (
          <PersonRow
            key={person.user.id}
            person={person}
            selected={selectePerson?.user.id === person.user.id}
            onSelected={() => onSelectPerson(person)}
          />
        ))}
      </div>
    </aside>
  );
};
